import * as THREE from 'three';
import { CameraMode } from '../defines';
import { CameraSettingType } from '../personas/CameraSettingsManager';
import type { View } from './View';
import type { Viewer } from './viewer';
import type { FloorplanPersona } from '../personas/personas/implementations/FloorplanPersona';

/**
 * Captured pose used to transition between 2D and 3D views without
 * snapping the user back to a default. Mirrors the shape that the global
 * ViewManager's `captureCameraState()` returned when this lived inline.
 */
export interface CapturedCameraState {
  position: THREE.Vector3;
  pivot: THREE.Vector3;
  yaw: number;
  pitch: number;
  roll: number;
  radius: number;
  cameraMode: (typeof CameraMode)[keyof typeof CameraMode];
}

/**
 * Capture a `View` (yaw/pitch/radius/position state, see `viewer/View.ts`)
 * + its current camera mode into a transferable snapshot. Used by both
 * the global ViewManager and per-RenderView widgets to record where the
 * camera was *before* switching views, so the 2D↔3D conversion can
 * preserve the framing.
 */
export function captureCameraState(view: View, cameraMode: (typeof CameraMode)[keyof typeof CameraMode]): CapturedCameraState {
  return {
    position: view.position.clone(),
    pivot: view.getPivot(),
    yaw: view.yaw,
    pitch: view.pitch,
    roll: view.roll,
    radius: view.radius,
    cameraMode,
  };
}

/**
 * Floor middle height for the currently-selected floor, if any. Used
 * by the 2D→3D conversion as the pivot Z when switching from a floor view.
 *
 * Lifted from `ViewManager.getFloorMiddleHeight()` so widgets reuse the
 * same logic. Returns null if there's no FloorplanPersona, no selected
 * floor, or the persona doesn't expose the helper.
 */
export function getFloorMiddleHeight(viewer: Viewer): number | null {
  if (!viewer.personaManager) return null;

  const floorplanPersona = viewer.personaManager.getPersona('floorplan');
  if (!floorplanPersona) return null;

  if (typeof (floorplanPersona as FloorplanPersona).getSelectedFloorData !== 'function') {
    return null;
  }
  const data = (floorplanPersona as FloorplanPersona).getSelectedFloorData();
  if (!data || !data.floor) return null;

  const floorHeight = data.floor.height;
  const ceilingHeight = data.ceiling?.height;
  if (ceilingHeight !== undefined && ceilingHeight !== null) {
    return (floorHeight + ceilingHeight) / 2;
  }
  return floorHeight;
}

/**
 * Apply a 2D→3D transition to `targetView`, preserving framing.
 *
 * Pure with respect to the target: mutates only `targetView`'s position /
 * yaw / pitch / roll / radius. Reads scene state for the pivot (floor
 * middle height for floor views, plane intersection for plane views,
 * Z=0 for map views).
 *
 * Note on the `planeId` lookup: the global ViewManager passes
 * `previousView?.viewContext?.planeId` here because by the time we
 * convert, `viewer.viewerMode.associatedPlane` may already have been
 * cleared. For the widget, pass the planeId of the view we're leaving
 * (or omit if the widget tracks its own associated plane).
 */
export async function convert2Dto3D(targetView: View, state: CapturedCameraState, viewer: Viewer, planeId?: string): Promise<void> {
  let targetPoint: THREE.Vector3;

  // First try floor middle height (floor-based views). Even widgets benefit
  // from this since they share the scene's floorplan persona.
  const floorMiddleHeight = getFloorMiddleHeight(viewer);

  // Look up the plane from planeId if provided.
  let associatedPlane = viewer.viewerMode?.associatedPlane;
  if (!associatedPlane && planeId && planeId !== 'map' && planeId !== 'side') {
    associatedPlane = viewer.scene.planes.find((p) => p.identifier === planeId) || null;
  }

  if (floorMiddleHeight !== null) {
    targetPoint = new THREE.Vector3(state.position.x, state.position.y, floorMiddleHeight);
  } else if (associatedPlane) {
    const planeThree = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane.normal, associatedPlane.position);

    const lookDir = new THREE.Vector3(0, 1, 0);
    lookDir.applyAxisAngle(new THREE.Vector3(1, 0, 0), state.pitch);
    lookDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), state.yaw);

    const rayForward = new THREE.Ray(state.position, lookDir.clone());
    const rayBackward = new THREE.Ray(state.position, lookDir.clone().negate());
    const intersectForward = new THREE.Vector3();
    const intersectBackward = new THREE.Vector3();
    const hitForward = rayForward.intersectPlane(planeThree, intersectForward);
    const hitBackward = rayBackward.intersectPlane(planeThree, intersectBackward);

    if (hitForward && hitBackward) {
      const distForward = state.position.distanceTo(intersectForward);
      const distBackward = state.position.distanceTo(intersectBackward);
      targetPoint = distForward < distBackward ? intersectForward : intersectBackward;
    } else if (hitForward) {
      targetPoint = intersectForward;
    } else if (hitBackward) {
      targetPoint = intersectBackward;
    } else {
      // Both rays missed: fall back to plane Z projected from camera XY.
      targetPoint = new THREE.Vector3(state.position.x, state.position.y, associatedPlane.position.z);
    }
  } else {
    // Top-down map view without a floor: pivot at Z=0.
    targetPoint = new THREE.Vector3(state.position.x, state.position.y, 0);
  }

  const distance = state.radius;
  const pitch3D = -Math.PI / 6; // 30° down

  // Synchronous read only — never await detection mid-transition (the switch
  // must apply its full pose in one tick). Routed through the orientation
  // manager so short-lived overrides are honored.
  const savedYaw = viewer.cameraOrientationManager?.effectiveHeading('perspective_3d');
  const yaw3D = savedYaw !== undefined ? savedYaw : state.yaw;

  const dir = new THREE.Vector3(0, 1, 0);
  dir.applyAxisAngle(new THREE.Vector3(1, 0, 0), pitch3D);
  dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), yaw3D);

  const newPosition = targetPoint.clone().sub(dir.multiplyScalar(distance));

  targetView.position.copy(newPosition);
  targetView.yaw = yaw3D;
  targetView.pitch = pitch3D;
  targetView.roll = 0;
  targetView.radius = distance;
}

/**
 * Apply a 3D→2D transition to `targetView`, preserving zoom and pivot.
 *
 * Mutates only `targetView`. Reads `viewer.cameraSettingsManager` for the
 * saved roll on top-down (map) views — non-top-down 2D views (floor, wall)
 * use their plane's orientation directly and ignore saved roll.
 */
export async function convert3Dto2D(targetView: View, state: CapturedCameraState, viewer: Viewer, targetCameraSettingType: CameraSettingType): Promise<void> {
  const targetPoint = state.pivot.clone();

  targetView.position.x = targetPoint.x;
  targetView.position.y = targetPoint.y;
  targetView.position.z = targetPoint.z + state.radius;
  targetView.radius = state.radius;
  targetView.yaw = 0;
  targetView.roll = 0;
  // Pitch -π/2 = camera looking straight down (along -Z). Required so that
  // `view.direction` resolves to (0,0,-1) and downstream orbit math
  // (`position = pivot - direction * radius`) keeps the camera above the
  // pivot. The main view's `enable2DMode*` writes the same value after
  // this helper runs; setting it here makes the helper standalone-correct
  // for widgets that don't go through `viewerMode.enable2DMode`.
  // Plane-aligned (non-top-down) 2D views overwrite pitch later from the
  // plane's inclination — TODO when the widget supports those views.
  targetView.pitch = -Math.PI / 2;

  if (targetCameraSettingType === CameraSettingType.ORTHOGRAPHIC_TOP) {
    // Synchronous read; detection (if still needed) runs in the background
    // and lands via the guarded safe-apply. Routed through the orientation
    // manager so short-lived overrides are honored.
    const heading = viewer.cameraOrientationManager?.effectiveHeading('top_down_2d');
    targetView.roll = heading !== undefined ? heading : 0;
    viewer.cameraOrientationManager?.ensureDetectedHeading();
  }
}
